import { ReservableChecker } from "./ReservableSlots.common.logic"
import { HourMinute } from "./time"

import { getFragmentData } from "@/main/gql/__generated__"
import { FragmentType } from "@/public/gql/__generated__"
import { DocumentType, graphql } from "@/public/gql/__generated__/gql"
import {
	addDate,
	dayEquals,
	newDateByGraphQLDayAndHourAndMinute,
} from "@/utils/date"

const dateReservableSlotsV2Fragment = graphql(/* GraphQL */ `
	fragment dateReservableSlotsV2 on DateReservableSlotsV2 {
		date
		reservableStartHour
		reservableStartMinute
		reservableEndHour
		reservableEndMinute
		reservableSlotsWithStaffChunks {
			reservableSlots {
				startHour
				startMinute
				durationMinute
				isUnavailableAsStartSlotIfStaggeredScheduling
			}
			staff {
				scrapingTargetSiteId
				name
			}
		}
		reservableSlotsWithEquipmentChunks {
			equipment {
				id
				name
			}
			reservableSlots {
				startHour
				startMinute
				durationMinute
			}
		}
	}
`)

const treatableStaffFragment = graphql(/* GraphQL */ `
	fragment treatableStaff on TreatableStaff {
		scrapingTargetSiteStaffId
		name
	}
`)

type ReservableSlots = DocumentType<
	typeof dateReservableSlotsV2Fragment
>["reservableSlotsWithStaffChunks"][number]["reservableSlots"]

type RenderableSlotData = {
	startTime: { hour: number; minute: number; formatHourMinuteObj(): string }
	// TODO: わかりづらいのでなんとかする
	slotType:
		| "CURRENT_STAFF_AVAILABLE" // 前回のスタッフで予約が可能 (設備チェックも考慮した上で)
		| "OTHER_STAFF_AVAILABLE" // 他のスタッフで予約が可能 (設備チェックも考慮した上で)
		| "NOT_AVAILABLE" // 予約が不可
	otherTreatableAvailableStaffs: {
		scrapingTargetSiteId: string
		name: string
	}[]
	// 予約作成 API に送信する設備IDの配列
	// - needSame=true: 前回予約と同じ設備IDすべて
	// - needAny=true: 予約可能な設備ID を一つだけ
	// - どちらも false: 空配列
	// NOTE: needSame と needAny が両方 true になることは想定していない。もし両方 true の場合は needSame を優先
	equipmentIdsToSubmit: string[]
}

// treatable: 技術的に予約可能なスタッフ
// available: 枠があいている
// 実際に予約可能な枠はavailableかつtreatable = tretableAvailable

export class SevenDaysReservableSlotsChunksV2 {
	sevenDaysChunks: {
		date: Date
		slots: RenderableSlotData[]
	}[]
	allChunks: DocumentType<typeof dateReservableSlotsV2Fragment>[]
	treatableStaffs: DocumentType<typeof treatableStaffFragment>[]
	needAnyReservableEquipmentToCreateReservation: boolean
	needSameEquipmentAsPreviousToCreateReservation: boolean
	previousEquipmentIds: string[]
	staggeredSchedulingEnabled: boolean

	private updateFields(
		startDay: Date,
		chunks: DocumentType<typeof dateReservableSlotsV2Fragment>[],
		originalStaffId: string | undefined,
		durationMinute: number
	) {
		const sevenDates = [
			startDay,
			addDate(startDay, 1),
			addDate(startDay, 2),
			addDate(startDay, 3),
			addDate(startDay, 4),
			addDate(startDay, 5),
			addDate(startDay, 6),
		]

		this.sevenDaysChunks = sevenDates.map((date) => ({
			date,
			slots: this.createRenderableSlotsForDate(
				date,
				chunks,
				originalStaffId,
				durationMinute
			),
		}))
	}

	private createRenderableSlotsForDate(
		date: Date,
		chunks: DocumentType<typeof dateReservableSlotsV2Fragment>[],
		originalStaffId: string | undefined,
		durationMinute: number
	): RenderableSlotData[] {
		const startTimes = this.getStartTimes()
		const dateChunk = chunks.find((chunk) => {
			const chunkDate = new Date(chunk.date)
			return dayEquals(date, chunkDate)
		})

		if (!dateChunk) {
			return startTimes.map((startTime) => ({
				date,
				startTime,
				slotType: "NOT_AVAILABLE",
				otherTreatableAvailableStaffs: [],
				equipmentIdsToSubmit: [],
			}))
		}

		const allStaffs = dateChunk.reservableSlotsWithStaffChunks.map((chunk) => ({
			scrapingTargetSiteId: chunk.staff.scrapingTargetSiteId,
			name: chunk.staff.name,
		}))

		return startTimes.map((startTime) => {
			const { passes: passesEquipmentCheck, equipmentIdsToSubmit } =
				this.checkEquipmentAvailability(dateChunk, startTime, durationMinute)

			const isCurrentStaffReservable = originalStaffId
				? this.isSlotReservableForStaff(
						dateChunk,
						originalStaffId,
						startTime,
						durationMinute,
						passesEquipmentCheck
					)
				: false

			const jstDate = newDateByGraphQLDayAndHourAndMinute(
				dateChunk.date,
				startTime.hour,
				startTime.minute
			)

			const otherTreatableAvailableStaffs = allStaffs.filter((staff) => {
				if (staff.scrapingTargetSiteId === originalStaffId) return false

				const isTreatable = this.treatableStaffs.some(
					(treatableStaff) =>
						treatableStaff.scrapingTargetSiteStaffId ===
						staff.scrapingTargetSiteId
				)
				if (!isTreatable) return false

				return this.isSlotReservableForStaff(
					dateChunk,
					staff.scrapingTargetSiteId,
					startTime,
					durationMinute,
					passesEquipmentCheck
				)
			})

			let slotType: RenderableSlotData["slotType"]
			if (isCurrentStaffReservable && originalStaffId) {
				slotType = "CURRENT_STAFF_AVAILABLE"
			} else if (otherTreatableAvailableStaffs.length > 0) {
				slotType = "OTHER_STAFF_AVAILABLE"
			} else {
				slotType = "NOT_AVAILABLE"
			}

			return {
				date: jstDate,
				startTime,
				slotType,
				otherTreatableAvailableStaffs,
				equipmentIdsToSubmit,
			}
		})
	}

	private getSlotsForStaffFromChunk(
		chunk: DocumentType<typeof dateReservableSlotsV2Fragment>,
		staffId: string
	): ReservableSlots {
		const slotsWithStaff = chunk.reservableSlotsWithStaffChunks.find(
			(slotsChunk) => {
				return slotsChunk.staff?.scrapingTargetSiteId === staffId
			}
		)
		return slotsWithStaff?.reservableSlots ?? []
	}

	// 指定した staffId のスタッフでそのスロットの予約が可能かを、以下の順序で判定する:
	// 1. スタッフの空き枠: startTime から durationMinute 分の連続した空きがあるか
	// 2. ずらし設定: ずらし設定により、この startTime が開始枠として使用不可でないか
	// 3. 設備の空き: 必要な設備が確保できるか（呼び出し元で事前に判定済み）
	private isSlotReservableForStaff(
		dateChunk: DocumentType<typeof dateReservableSlotsV2Fragment>,
		staffId: string,
		startTime: HourMinute,
		durationMinute: number,
		passesEquipmentCheck: boolean
	): boolean {
		const slots = this.getSlotsForStaffFromChunk(dateChunk, staffId)
		const checker = new ReservableChecker(slots)
		if (!checker.isReservable(startTime, durationMinute)) {
			return false
		}
		if (this.isSlotBlockedByStagger(slots, startTime)) {
			return false
		}
		return passesEquipmentCheck
	}

	private isSlotBlockedByStagger(
		slots: ReservableSlots,
		startTime: HourMinute
	): boolean {
		if (!this.staggeredSchedulingEnabled) {
			return false
		}

		const matchingSlot = slots.find(
			(slot) =>
				slot.startHour === startTime.hour &&
				slot.startMinute === startTime.minute
		)

		return matchingSlot?.isUnavailableAsStartSlotIfStaggeredScheduling ?? false
	}

	// 指定した設備が、startTime から durationMinute 分連続で空いているかチェック
	private isEquipmentReservableForSlot(
		dateChunk: DocumentType<typeof dateReservableSlotsV2Fragment>,
		equipmentId: string,
		startTime: HourMinute,
		durationMinute: number
	): boolean {
		const equipmentChunk = dateChunk.reservableSlotsWithEquipmentChunks.find(
			(chunk) => chunk.equipment.id === equipmentId
		)
		if (!equipmentChunk) {
			return false
		}
		const checker = new ReservableChecker(equipmentChunk.reservableSlots)
		return checker.isReservable(startTime, durationMinute)
	}

	// dateChunk.date, startTime から始まる枠で、durationMinute 分の連続した空き枠がある設備 ID を返す
	private getReservableFirstEquipmentIdForSlot(
		dateChunk: DocumentType<typeof dateReservableSlotsV2Fragment>,
		startTime: HourMinute,
		durationMinute: number
	): string | null {
		for (const equipmentChunk of dateChunk.reservableSlotsWithEquipmentChunks) {
			if (
				this.isEquipmentReservableForSlot(
					dateChunk,
					equipmentChunk.equipment.id,
					startTime,
					durationMinute
				)
			) {
				return equipmentChunk.equipment.id
			}
		}
		return null
	}

	// 前回予約の設備が、この枠で durationMinute 分連続で空いているかチェック
	// previousEquipmentIds のすべてが空いている必要がある
	private arePreviousEquipmentsAvailableForSlot(
		dateChunk: DocumentType<typeof dateReservableSlotsV2Fragment>,
		startTime: HourMinute,
		durationMinute: number
	): boolean {
		if (this.previousEquipmentIds.length === 0) {
			return true
		}

		return this.previousEquipmentIds.every((equipmentId) =>
			this.isEquipmentReservableForSlot(
				dateChunk,
				equipmentId,
				startTime,
				durationMinute
			)
		)
	}

	// 設備チェックをパスするかどうかと、送信する設備IDを返す
	private checkEquipmentAvailability(
		dateChunk: DocumentType<typeof dateReservableSlotsV2Fragment>,
		startTime: HourMinute,
		durationMinute: number
	): { passes: boolean; equipmentIdsToSubmit: string[] } {
		// needSame: 前回と同じ設備がすべて空いている必要がある（優先）
		if (this.needSameEquipmentAsPreviousToCreateReservation) {
			const passes = this.arePreviousEquipmentsAvailableForSlot(
				dateChunk,
				startTime,
				durationMinute
			)
			return {
				passes,
				equipmentIdsToSubmit: passes ? this.previousEquipmentIds : [],
			}
		}

		// needAny: 何かしらの設備が一個だけ空いていればOK
		if (this.needAnyReservableEquipmentToCreateReservation) {
			const reservableFirstEquipmentId =
				this.getReservableFirstEquipmentIdForSlot(
					dateChunk,
					startTime,
					durationMinute
				)
			return {
				passes: reservableFirstEquipmentId !== null,
				equipmentIdsToSubmit: reservableFirstEquipmentId
					? [reservableFirstEquipmentId]
					: [],
			}
		}

		// どちらも false: 設備チェック不要
		return { passes: true, equipmentIdsToSubmit: [] }
	}

	constructor(
		startDay: Date,
		chunks: FragmentType<typeof dateReservableSlotsV2Fragment>[],
		originalStaffId: string | undefined,
		durationMinute: number,
		treatableStaffs: FragmentType<typeof treatableStaffFragment>[],
		needAnyReservableEquipmentToCreateReservation: boolean,
		needSameEquipmentAsPreviousToCreateReservation: boolean,
		previousEquipmentIds: string[],
		staggeredSchedulingEnabled: boolean
	) {
		this.allChunks = chunks.map((chunk) => {
			return getFragmentData(dateReservableSlotsV2Fragment, chunk)
		})
		this.treatableStaffs = treatableStaffs.map((staff) => {
			return getFragmentData(treatableStaffFragment, staff)
		})
		this.needAnyReservableEquipmentToCreateReservation =
			needAnyReservableEquipmentToCreateReservation
		this.needSameEquipmentAsPreviousToCreateReservation =
			needSameEquipmentAsPreviousToCreateReservation
		this.previousEquipmentIds = previousEquipmentIds
		this.staggeredSchedulingEnabled = staggeredSchedulingEnabled
		this.sevenDaysChunks = []
		this.updateFields(startDay, this.allChunks, originalStaffId, durationMinute)
	}

	private getMinStartTime(): HourMinute {
		let minStartTime = new HourMinute(11, 0) // 遅くても11時から表示
		this.allChunks.forEach((slots) => {
			const hourMinute = new HourMinute(
				slots.reservableStartHour,
				slots.reservableStartMinute
			)
			if (hourMinute.less(minStartTime)) {
				minStartTime = hourMinute
			}
		})
		return minStartTime
	}

	private getMaxStartTime(): HourMinute {
		let maxStartTime = new HourMinute(20, 0) // 早くても20時まで表示
		this.allChunks.forEach((slots) => {
			if (!slots) {
				return
			}

			const hourMinute = new HourMinute(
				slots.reservableEndHour,
				slots.reservableEndMinute
			)
			if (maxStartTime.less(hourMinute)) {
				maxStartTime = hourMinute
			}
		})
		return maxStartTime.addMinute(-30) // (max reservableEnd time) - 30
	}

	getStartTimes(): HourMinute[] {
		const minStartTime = this.getMinStartTime()
		const maxStartTime = this.getMaxStartTime()

		const startTimes: HourMinute[] = []
		for (
			let hourMinute = minStartTime;
			hourMinute.less(maxStartTime);
			hourMinute = hourMinute.addMinute(30)
		) {
			startTimes.push(hourMinute)
		}
		startTimes.push(maxStartTime)

		return startTimes
	}
}
